import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { ComboboxInputField, DateInputField, FormattedNumberInputField } from "@/components/shared/form";
import { CircleLoader } from "@/components/shared/loaders";
import { convertDecimalToBp } from "@/utils/helpers";
import { useEffect } from "react";
import useAddVacationBonusMutationHook from "@/hooks/mutations/useAddVacationBonus";
import { provincesSimplified } from "@/constants/provinces";

const createVacationBonusSchema = (t: TFunction) => z.object({
  province: z.string({ message: t("schemas:form.required") }),
  activeFrom: z.date({
    error: t("schemas:form.required"),
  }),
  bonusRelative: z.number({
    message: t("schemas:form.required"),
  }).min(0, t("schemas:form.atleast0")).max(100, t("schemas:form.atmax100")),
  bonusAbsolute: z.number({
    message: t("schemas:form.required"),
  })
});

interface AddVacationBonusDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
}

/**
 * AddVacationBonusDialog component for adding a new vacation bonus
 * @param props Component properties
 * @returns JSX element
 */
function AddVacationBonusDialog({ open, onOpenChange }: AddVacationBonusDialogProps) {
  const { t } = useTranslation(["modals", "common"]);

  const { addVacationBonusMutation, loading: createVacationBonusLoading } = useAddVacationBonusMutationHook();

  const vacationBonusSchema = createVacationBonusSchema(t);
  const form = useForm<z.infer<typeof vacationBonusSchema>>({
    resolver: zodResolver(vacationBonusSchema),
    defaultValues: {
      province: undefined,
      activeFrom: undefined,
      bonusRelative: undefined,
      bonusAbsolute: undefined,
    },
    mode: "onChange",
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      form.reset({
        province: undefined,
        activeFrom: undefined,
        bonusRelative: undefined,
        bonusAbsolute: undefined,
      });
    }
  }, [open, form]);

  const onSubmit = (data: z.infer<typeof vacationBonusSchema>) => {

    // Convert the value from decimal to basis points
    const valueInBp = convertDecimalToBp(data.bonusRelative);

    const processedData = {
      ...data,
      province: data.province,
      bonusRelative: valueInBp,
      bonusAbsolute: data.bonusAbsolute,
    };

    addVacationBonusMutation({
      variables: { input: processedData },
    }).then(() => {
      onOpenChange(false);
    });
  };

  const onInvalid = (errors: FieldErrors<z.infer<typeof vacationBonusSchema>>) => {
    console.error(errors);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="add-vacation-bonus-form">
            <DialogHeader>
              <DialogTitle>
                {t("vacationbonus.title")}
              </DialogTitle>
              <DialogDescription>
                {t("vacationbonus.description")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <DateInputField
                  form={form}
                  name="activeFrom"
                  label={t("vacationbonus.inputs.activationdate.label")}
                  className="w-full"
                  allowOlderDates={true}
                />
              </div>

              {/* Province */}
              <ComboboxInputField
                form={form}
                name="province"
                label={t("vacationbonus.inputs.province.label")}
                options={provincesSimplified.map((province) => ({
                  label: province.name,
                  value: province.code
                }))}
                className="w-full"
              />

              <FormattedNumberInputField
                control={form.control}
                name="bonusRelative"
                label={t("vacationbonus.inputs.bonusrelative.label")}
                placeholder={t("vacationbonus.inputs.bonusrelative.placeholder")}
                className="w-full"
                style="decimal"
                additionalSuffix="%"
                decimalScale={2}
                isStandalone={true}
              />

              <FormattedNumberInputField
                control={form.control}
                name="bonusAbsolute"
                label={t("vacationbonus.inputs.bonusabsolute.label")}
                placeholder={t("vacationbonus.inputs.bonusabsolute.placeholder")}
                className="w-full"
                style="currency"
                decimalScale={2}
                isStandalone={true}
              />

            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button">
                  {t("common:buttons.close")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={createVacationBonusLoading}>
                <CircleLoader visible={createVacationBonusLoading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddVacationBonusDialog;
